"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Cpu, Gauge, Layers, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const specifications = [{
  icon: Gauge,
  label: "Spectral Range",
  value: "950 – 1650 nm",
  detail: "Linear variable filter (LVF)"
}, {
  icon: Cpu,
  label: "Interface & Power",
  value: "USB 2.0 / USB-C",
  detail: "Direct PC integration"
}, {
  icon: ShieldCheck,
  label: "Enclosure Rating",
  value: "IP65 / IP67",
  detail: "Washdown compliant design"
}, {
  icon: Layers,
  label: "Primary Applications",
  value: "Blend & Drying",
  detail: "Real-time process monitoring"
}];
const keyPoints = ["Non-invasive real-time NIR process measurement", "Compact form factor for direct port integration", "Validated for pharmaceutical and regulated environments"];
export default function MicroNirHero() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="heromicronirpatu" data-nav="light" className="relative bg-background text-foreground py-16 lg:py-24 border-b border-border overflow-hidden">
      {/* Subtle decorative grid background */}
      <div className="absolute inset-0 bg-[radial-gradient(hsl(var(--border))_1px,transparent_1px)] [background-size:24px_24px] opacity-40 pointer-events-none" />

      <div className="page-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: Copy & Actions */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="text-xs font-medium px-3 py-1">
                VIAVI Solutions MicroNIR Series
              </Badge>
              <Badge variant="outline" className="text-xs font-medium px-3 py-1 text-muted-foreground">
                PAT-U Instrument
              </Badge>
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default leading-tight">
                MicroNIR PAT-U Process Spectrometer
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
                A compact near-infrared spectrometer designed for in-line and at-line process analytical technology (PAT) applications in pharmaceutical and chemical manufacturing.
              </p>
            </div>

            {/* Key benefits list */}
            <div className="space-y-2.5 py-2">
              {keyPoints.map((point, index) => <div key={index} data-index={index} className="flex items-center gap-3">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span className="text-sm text-foreground">{point}</span>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button asChild size="lg" className="h-11 px-6">
                <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                  <span>Discuss Your Application</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-11 px-6">
                <Link to="/BookADemoPage">
                  Book Instrument Demo
                </Link>
              </Button>
            </div>

            <div className="pt-2 text-xs text-muted-foreground flex items-center gap-2">
              <span>Supported across Ireland &amp; the UK by Antech Solutions.</span>
              <span>•</span>
              <Link to="/ProcessAnalyticalTechnologyPage" className="text-primary hover:underline font-medium">
                Learn about PAT Solutions
              </Link>
            </div>
          </div>

          {/* Right Column: Instrument visual & key specs */}
          <div className="lg:col-span-5">
            <motion.div initial={shouldReduceMotion ? {} : {
            opacity: 0,
            y: 16
          }} animate={shouldReduceMotion ? {} : {
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="rounded-2xl border border-border bg-card p-6 shadow-md relative">
              <div className="relative aspect-4/3 w-full rounded-lg overflow-hidden bg-muted/40 mb-6 flex items-center justify-center border border-border/60">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_u_spectrometer.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_u_spectrometer.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_u_spectrometer.png-wvc-sizes" alt="MicroNIR PAT-U NIR Process Spectrometer" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background/90 backdrop-blur-xs border border-border text-xs font-mono text-foreground font-medium shadow-2xs">
                    <span className="size-2 rounded-full bg-emerald-500 inline-block" />
                    USB-Powered
                  </span>
                </div>
              </div>

              {/* Technical Specifications Grid */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                {specifications.map((spec, index) => {
                const Icon = spec.icon;
                return <div key={index} data-index={index} className="rounded-lg bg-secondary/50 p-3 border border-border/50">
                      <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                        <Icon className="size-3.5 text-primary" />
                        <span>{spec.label}</span>
                      </div>
                      <div className="font-semibold text-sm text-foreground">
                        {spec.value}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                        {spec.detail}
                      </div>
                    </div>;
              })}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}